import apiClient from './api'

export const reportService = {
  /**
   * Obtener resumen de IVA mensual
   * @param {Object} params - { company_id, month, year }
   */
  async getVatSummary(params) {
    try {
      const response = await apiClient.get('/reports/vat-summary', { params })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Error fetching VAT summary:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener el resumen de IVA'
      }
    }
  },

  /**
   * Exportar resumen de IVA a Excel
   * @param {Object} params - { company_id, month, year }
   */
  async exportVatSummary(params) {
    try {
      const response = await apiClient.get('/reports/vat-summary/export', { 
        params,
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `resumen_iva_${params.month}_${params.year}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting VAT summary:', error)
      return {
        success: false,
        message: 'Error al exportar el reporte'
      }
    }
  },

  /**
   * Obtener reporte de ventas
   * @param {Object} params - { company_id, start_date, end_date, document_type }
   */
  async getSalesReport(params) {
    try {
      const response = await apiClient.get('/reports/sales', { params })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Error fetching sales report:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener el reporte de ventas'
      }
    }
  },

  /**
   * Exportar reporte de ventas a Excel (resumen por documento)
   * @param {Object} params - { company_id, start_date, end_date, document_type }
   */
  async exportSalesReport(params) {
    try {
      const response = await apiClient.get('/reports/sales/export', {
        params,
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `reporte_ventas_${params.start_date}_${params.end_date}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      return { success: true }
    } catch (error) {
      console.error('Error exporting sales report:', error)
      return {
        success: false,
        message: 'Error al exportar el reporte'
      }
    }
  },

  /**
   * Exportar reporte de ventas detallado con productos a Excel
   * @param {Object} params - { company_id, start_date, end_date, document_type }
   */
  async exportDetailedSalesReport(params) {
    try {
      const response = await apiClient.get('/reports/sales/export-detailed', {
        params,
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `reporte_ventas_con_productos_${params.start_date}_${params.end_date}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      return { success: true }
    } catch (error) {
      console.error('Error exporting detailed sales report with products:', error)
      return {
        success: false,
        message: 'Error al exportar el reporte con productos'
      }
    }
  },

  /**
   * Obtener reporte de gastos por categoría
   * @param {Object} params - { company_id, start_date, end_date }
   */
  async getExpensesReport(params) {
    try {
      const response = await apiClient.get('/reports/expenses', { params })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Error fetching expenses report:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener el reporte de gastos'
      }
    }
  },

  /**
   * Reportes Financieros Enterprise
   */
  async getBalanceSheet(companyId, atDate, params = {}) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/balance-sheet`, {
        params: { at_date: atDate, ...params }
      })
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al obtener Balance General' }
    }
  },

  async getProfitAndLoss(companyId, fromDate, toDate, params = {}) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/profit-and-loss`, {
        params: { from_date: fromDate, to_date: toDate, ...params }
      })
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al obtener Estado de Resultados' }
    }
  },

  async getGeneralLedger(companyId, accountId, fromDate, toDate) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/general-ledger`, {
        params: { account_id: accountId, from_date: fromDate, to_date: toDate }
      })
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al obtener Libro Mayor' }
    }
  },

  async getPortfolioEntityStatement(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/portfolio/statement`, {
        params
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error al obtener Estado de Cuenta' };
    }
  },

  async exportEntityStatement(companyId, params) {
    return apiClient.get(`/companies/${companyId}/reports/portfolio/statement`, {
      params,
      responseType: 'blob'
    });
  },

  async exportBalanceSheet(companyId, atDate, params = {}) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/balance-sheet/export`, {
        params: { at_date: atDate, ...params },
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `balance_general_${atDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting Balance Sheet:', error)
      return {
        success: false,
        message: 'Error al exportar el Balance General'
      }
    }
  },

  async exportProfitAndLoss(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/profit-and-loss/export`, {
        params,
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fromDate = params.from_date || 'inicio'
      const toDate = params.to_date || 'fin'
      link.setAttribute('download', `estado_resultados_${fromDate}_a_${toDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting Profit and Loss:', error)
      return {
        success: false,
        message: 'Error al exportar el Estado de Resultados'
      }
    }
  },

  async exportProfitAndLossMonthly(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/profit-and-loss/export`, {
        params: { ...params, monthly: true },
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fromDate = params.from_date || 'inicio'
      const toDate = params.to_date || 'fin'
      link.setAttribute('download', `estado_resultados_mensualizado_${fromDate}_a_${toDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting Profit and Loss monthly:', error)
      return {
        success: false,
        message: 'Error al exportar el Estado de Resultados mensualizado'
      }
    }
  },

  async exportGeneralLedger(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/general-ledger/export`, {
        params,
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fromDate = params.from_date || 'inicio'
      const toDate = params.to_date || 'fin'
      link.setAttribute('download', `mayor_contable_${params.account_id}_${fromDate}_a_${toDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting General Ledger Excel:', error)
      return {
        success: false,
        message: 'Error al exportar el Libro Mayor a Excel'
      }
    }
  },

  async exportGeneralLedgerPdf(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/general-ledger/pdf`, {
        params,
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fromDate = params.from_date || 'inicio'
      const toDate = params.to_date || 'fin'
      link.setAttribute('download', `mayor_contable_${params.account_id}_${fromDate}_a_${toDate}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting General Ledger PDF:', error)
      return {
        success: false,
        message: 'Error al exportar el Libro Mayor a PDF'
      }
    }
  },

  async exportBalanceSheetPdf(companyId, atDate, params = {}) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/balance-sheet/pdf`, {
        params: { at_date: atDate, ...params },
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `balance_general_${atDate}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting Balance Sheet PDF:', error)
      return {
        success: false,
        message: 'Error al exportar el Balance General a PDF'
      }
    }
  },

  async exportProfitAndLossPdf(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/profit-and-loss/pdf`, {
        params,
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fromDate = params.from_date || 'inicio'
      const toDate = params.to_date || 'fin'
      link.setAttribute('download', `estado_resultados_${fromDate}_a_${toDate}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting Profit and Loss PDF:', error)
      return {
        success: false,
        message: 'Error al exportar el Estado de Resultados a PDF'
      }
    }
  },

  async exportProfitAndLossMonthlyPdf(companyId, params) {
    try {
      const response = await apiClient.get(`/companies/${companyId}/reports/financial/profit-and-loss/pdf`, {
        params: { ...params, monthly: true },
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fromDate = params.from_date || 'inicio'
      const toDate = params.to_date || 'fin'
      link.setAttribute('download', `estado_resultados_mensualizado_${fromDate}_a_${toDate}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting Profit and Loss monthly PDF:', error)
      return {
        success: false,
        message: 'Error al exportar el Estado de Resultados mensualizado a PDF'
      }
    }
  },

  /**
   * Obtener reporte detallado de compras y ventas
   * @param {Object} params - { company_id, start_date, end_date }
   */
  async getDetailedPurchasesSales(params) {
    try {
      const response = await apiClient.get('/reports/purchases-sales/detailed', { params })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Error fetching detailed report:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener el reporte detallado'
      }
    }
  },

  /**
   * Exportar reporte detallado de compras y ventas a Excel
   * @param {Object} params - { company_id, start_date, end_date }
   */
  async exportDetailedPurchasesSales(params) {
    try {
      const response = await apiClient.get('/reports/purchases-sales/detailed/export', {
        params,
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `reporte_detallado_compras_ventas_${params.start_date}_a_${params.end_date}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      return { success: true }
    } catch (error) {
      console.error('Error exporting detailed report:', error)
      return {
        success: false,
        message: 'Error al exportar el reporte detallado'
      }
    }
  }
}

